import React, { useContext } from 'react';
import {
  IRapportMallJson,
  parseRapportMallJson,
} from '../../../../../../BaseComponents/RapportMall/RapportMall';
import { BusinessDataFormContext } from '../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/BusinessDataForm';
import { EDTextEdit } from '../../../../../../BaseComponents/TextEdit/EDTextEdit';
import EDCheckbox from '../../../../../../BaseComponents/Checkboxes/EDCheckbox';
import EDBox from '../../../../../../BaseComponents/EDBox/EDBox';
import EDTypography from '../../../../../../BaseComponents/Typography/EDTypography';

interface IProps {}

export const RapportmallRootProperties: React.FC<IProps> = () => {
  const businessDataFormContext = useContext(BusinessDataFormContext);

  const mall = businessDataFormContext?.formState.currentValues.PdfJson;
  const parsedMall = parseRapportMallJson(mall);

  const setMall = (newMall: string) => {
    businessDataFormContext?.update([
      {
        operation: 'OwnFields',
        values: { PdfJson: newMall.replaceAll('\n', '\r\n') },
      },
    ]);
  };

  const updateProperty = (newValues: { [key: string]: any }) => {
    if (!parsedMall) return;
    // Body ska alltid vara sista egenskapen i JSON.
    const { Body: existingBody, ...restExisting } = parsedMall;
    const updatedMall: IRapportMallJson = {
      ...restExisting,
      ...newValues,
      Body: existingBody,
    };
    // Om WatermarkSvk är true ska Watermark tas bort helt.
    if (updatedMall.WatermarkSvk) {
      delete updatedMall.Watermark;
    }
    setMall(JSON.stringify(updatedMall, null, 2));
  };

  const numberOrUndefined = (value: string): number | undefined => {
    if (value === '') return undefined;
    const num = Number(value);
    return isNaN(num) ? undefined : num;
  };

  return (
    <EDBox
      sx={{
        display: 'grid',
        rowGap: 0,
        columnGap: 2,
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        alignItems: 'start',
      }}
    >
      {/* Titel (normal bredd) + radbrytare */}
      <EDTextEdit
        label="Titel"
        value={parsedMall?.Title ?? ''}
        onChange={value => updateProperty({ Title: value })}
      />
      {/* Standardinställningar */}
      <EDBox
        sx={{
          gridColumnStart: '1',
        }}
      >
        <EDTextEdit
          label="Standardtypsnitt"
          value={parsedMall?.DefaultFontName ?? ''}
          onChange={value => updateProperty({ DefaultFontName: value })}
        />
      </EDBox>
      <EDTextEdit
        label="Standardtextstorlek"
        value={String(parsedMall?.DefaultFontSize ?? '')}
        onChange={value =>
          updateProperty({ DefaultFontSize: numberOrUndefined(value) })
        }
      />
      {/* Marginalrubrik helbredd */}
      <EDBox
        sx={{
          gridColumn: '1 / -1',
        }}
      >
        <EDTypography marginTop={2}>Sidmarginaler (mm)</EDTypography>
      </EDBox>
      {/* Marginaler */}
      <EDTextEdit
        label="Vänster"
        value={String(parsedMall?.PageMarginLeft ?? '')}
        onChange={value =>
          updateProperty({ PageMarginLeft: numberOrUndefined(value) })
        }
      />
      <EDTextEdit
        label="Topp"
        value={String(parsedMall?.PageMarginTop ?? '')}
        onChange={value =>
          updateProperty({ PageMarginTop: numberOrUndefined(value) })
        }
      />
      <EDTextEdit
        label="Höger"
        value={String(parsedMall?.PageMarginRight ?? '')}
        onChange={value =>
          updateProperty({ PageMarginRight: numberOrUndefined(value) })
        }
      />
      <EDTextEdit
        label="Botten"
        value={String(parsedMall?.PageMarginBottom ?? '')}
        onChange={value =>
          updateProperty({ PageMarginBottom: numberOrUndefined(value) })
        }
      />
      {/* Sidnummer & Logo */}
      <EDBox sx={{ gridColumnStart: '1' }}>
        <EDCheckbox
          label="Sidnummer i sidhuvud"
          checked={!!parsedMall?.PageNoInHeader}
          onCheck={checked => updateProperty({ PageNoInHeader: checked })}
        />
        <EDCheckbox
          label="Sidnummer i sidfot"
          checked={!!parsedMall?.PageNoInFooter}
          onCheck={checked => updateProperty({ PageNoInFooter: checked })}
        />
      </EDBox>
      <EDCheckbox
        label="Standardlayout"
        checked={parsedMall?.Layout !== false}
        onCheck={checked => updateProperty({ Layout: checked })}
      />
      <EDCheckbox
        label="Linje ovanför sidfot"
        checked={!!parsedMall?.FooterSeparator}
        onCheck={checked => updateProperty({ FooterSeparator: checked })}
      />
      {/* Vattenstämpel */}
      <EDBox sx={{ gridColumnStart: '1', marginTop: 1.5 }}>
        <EDCheckbox
          label="Svk standard dokumentID"
          checked={!!parsedMall?.WatermarkSvk}
          onCheck={checked => updateProperty({ WatermarkSvk: checked })}
        />
      </EDBox>
      {!parsedMall?.WatermarkSvk && (
        <EDBox sx={{ gridColumn: '2 / 5' }}>
          <EDTextEdit
            label="DokumentID"
            value={parsedMall?.Watermark ?? ''}
            onChange={value => updateProperty({ Watermark: value })}
          />
        </EDBox>
      )}
    </EDBox>
  );
};
